
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import CompetitionEventsItem from '@/components/sports/common/overview/calendar/CompetitionEventsItem.vue';

import { CollectionType } from '@/enums/collection-type';

import { isToday, shiftDateWithTimezone } from '@/helpers/date-helper';
import { trackClickOutSports } from '@/helpers/offer-helper';

import { Platform, SportCompetitionV2, SportEventAggregation, SportEventBucketsEdgeV2 } from '@/@types/graphql-types';
import {
	GetSingleStepSportOverviewDocument,
	GetSingleStepSportOverviewQueryVariables as SingleStepSportVariables,
} from '@/pages/graphql/queries/GetSingleStepSportOverview.query';

import type { SportOffer } from '@/@types/graphql-types';
import { LocaleResponse } from '@/helpers/locale-helper';
import { contextIs, SingleStepContext } from '@/helpers/sports/config';
import type { SnowplowContext } from '@/helpers/tracking/providers';
import type { FilterCollection } from '@/stores/filter.store';

const Filter = namespace('filter');
const Language = namespace('language');

@Component({
	name: 'EmptyCalendar',
	components: {
		CompetitionEventsItem,
	},
	apollo: {
		fallbackData: {
			query: GetSingleStepSportOverviewDocument,
			variables(): SingleStepSportVariables {
				return this.queryVariables;
			},
			update(data) {
				return data;
			},
			skip() {
				return this.$route.name !== 'app.sports.overview';
			},
		},
	},
})
export default class EmptyCalendar extends Vue {
	fallbackData: SportCompetitionV2 = {} as SportCompetitionV2;

	@Prop({ required: true }) displayDate: Date;
	@Prop({ default: 7 }) daysLookAhead: number;
	@Prop({ required: true }) context: SingleStepContext;
	@Prop({ default: () => [] }) additionalContexts: SnowplowContext[];

	@Filter.Getter(CollectionType.SPORTS) filtersSport: FilterCollection;

	@Language.Getter country: string;
	@Language.Getter language: string;
	@Language.Getter localeObject: LocaleResponse;

	get queryVariables(): SingleStepSportVariables {
		return {
			language: this.language,
			country: this.country,
			platform: Platform.Web,
			groupBy: SportEventAggregation.Competition,
			bucketSize: 3,
			first: 2,
			filter: {
				startAt: {
					from: shiftDateWithTimezone(this.localeObject.timezone, 0, this.displayDate).toISOString(),
					to: shiftDateWithTimezone(
						this.localeObject.timezone,
						this.daysLookAhead,
						this.displayDate
					).toISOString(),
				},
				packages: this.filtersSport.providers ?? [],
				sports: [this.context.sportType],
			},
			timezoneOffset: 0,
		};
	}

	get eventBuckets() {
		return this.fallbackData?.sportEventBucketsV2;
	}

	get competitions() {
		return this.eventBuckets?.edges ?? [];
	}

	get messageText() {
		if (isToday(this.displayDate)) {
			return this.$t('SPORTS_WEBAPP_NO_EVENTS_TODAY');
		}

		const locale = `${this.language}-${this.country}`;
		const date = new Intl.DateTimeFormat(locale).format(this.displayDate);
		return this.$t('SPORTS_WEBAPP_NO_EVENTS_DATE', { date });
	}

	getEmptyCompetitionHeading(competition: SportEventBucketsEdgeV2) {
		if (contextIs.overview(this.context)) {
			return this.context.getCompetitionHeading(competition.key, this.$t.bind(this));
		}
	}

	handleClickout(offerLocation: 'event-card', offer: SportOffer) {
		trackClickOutSports(offer, 0, 0, offer.sportObjectType, offerLocation, this.additionalContexts);
	}
}
